import React from 'react';
import { CheckCircle, Package, MapPin, Clock, CreditCard, Home, ChevronRight, Tag, Wallet } from 'lucide-react';
import { getOrderItemImage } from '../../utils/imageHelpers';

const formatOrderDate = () => {
  const d = new Date();
  const weekday = d.toLocaleDateString('en-IN', { weekday: 'long' });
  const day = d.getDate();
  const month = d.toLocaleDateString('en-IN', { month: 'long' });
  const year = d.getFullYear();
  const hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  const hour12 = (hours % 12 || 12).toString().padStart(2, '0');
  return `${weekday}, ${day} ${month} ${year} at ${hour12}:${minutes}${ampm}`;
};

const DEFAULT_SLOTS = [
  { id: 'morning',   label: 'Morning',   time: '6:00 AM – 8:00 AM', icon: '🌅', cutoffHour: 4  },
  { id: 'afternoon', label: 'Afternoon', time: '2:00 PM – 4:00 PM', icon: '☀️', cutoffHour: 12 },
  { id: 'evening',   label: 'Evening',   time: '6:00 PM – 8:00 PM', icon: '🌆', cutoffHour: 16 },
];

const isSlotEnabled = (s) =>
  s.enabled !== false && s.enabled !== 0 && s.enabled !== '0' && s.enabled !== 'false';

const buildSlotMap = (slots) =>
  (slots && slots.length ? slots : DEFAULT_SLOTS)
    .filter(isSlotEnabled)
    .reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

// Returns the next available calendar date for a given slot.
// Orders placed before the slot's cutoffHour are eligible for same-day delivery;
// otherwise the next day is returned.
const getNextSlotDate = (slotId, slotMap) => {
  const map = slotMap || buildSlotMap(null);
  const slot = map[slotId] ?? map.morning ?? DEFAULT_SLOTS[0];
  const now = new Date();
  const date = new Date(now);
  if (now.getHours() >= (slot.cutoffHour ?? 0)) {
    date.setDate(date.getDate() + 1);
  }
  return date;
};

const formatDeliveryDate = (date) => {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dayLabel =
    date.toDateString() === now.toDateString()     ? 'Today' :
    date.toDateString() === tomorrow.toDateString() ? 'Tomorrow' :
    date.toLocaleDateString('en-IN', { weekday: 'long' });

  const dateStr = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return `${dayLabel}, ${dateStr}`;
};

const paymentMethodLabels = {
  cod:      { label: 'Cash on Delivery',           icon: '💵' },
  upi:      { label: 'UPI Payment',                icon: '📱' },
  card:     { label: 'Card Payment',               icon: '💳' },
  wallet:   { label: 'Wallet',                     icon: '👛' },
  upi_cod:  { label: 'Cash on Delivery (UPI)',     icon: '📱' },
  razorpay: { label: 'Razorpay (Online)',         icon: '⚡' },
  payu:     { label: 'PayU (Online)',             icon: '🔒' },
  netbanking:{ label: 'Net Banking',               icon: '🏦' },
  online:   { label: 'Online Payment',            icon: '🔒' },
};

const OrderConfirmationModal = ({
  isOpen,
  orderDetails,
  onViewOrders,
  onContinueShopping,
  onContactSupport,
  upiQrCode = '/static/upi-qr.jpeg',
  upiId = '',
  deliverySlots,
}) => {
  if (!isOpen) return null;

  const {
    orderId,
    orderNumber,
    items,
    totalAmount,
    originalAmount,
    couponCode,
    couponDiscount,
    shippingCharge,
    taxAmount,
    walletAmountUsed,
    deliverySlot,
    paymentMethod,
    deliveryAddress,
    estimatedDelivery,
  } = orderDetails || {};

  const slotMap = buildSlotMap(deliverySlots);
  const selectedSlot = slotMap[deliverySlot] || slotMap.morning || DEFAULT_SLOTS[0];
  const slotDeliveryDate = getNextSlotDate(deliverySlot, slotMap);
  const formattedDeliveryDate = formatDeliveryDate(slotDeliveryDate);

  // Normalize a variety of backend/frontend payment identifiers into our label keys
  const normalizePaymentKey = (raw) => {
    if (!raw) return null;
    const r = String(raw).toLowerCase().trim();
    if (r === 'cod') return 'cod';
    if (r === 'upi_cod' || (r.includes('upi') && r.includes('cod'))) return 'upi_cod';
    if (r.includes('razor')) return 'razorpay';
    if (r.includes('payu')) return 'payu';
    if (r.includes('wallet')) return 'wallet';
    if (r.includes('card')) return 'card';
    if (r.includes('netbank')) return 'netbanking';
    if (r.includes('upi')) return 'upi';
    if (r.includes('online')) return 'online';
    return r;
  };

  const rawPayment = orderDetails?.paymentMethod || orderDetails?.payment_method || orderDetails?.payment_gateway || paymentMethod;
  const normalizedKey = normalizePaymentKey(rawPayment) || (walletAmountUsed > 0 ? 'wallet' : 'cod');
  const selectedPayment = paymentMethodLabels[normalizedKey] || paymentMethodLabels.cod;

  // Derive a friendly gateway/provider label to show alongside the payment method
  const gatewayMap = {
    razorpay: 'Razorpay',
    payu: 'PayU',
    stripe: 'Stripe',
    paypal: 'PayPal',
  };

  const rawGateway = orderDetails?.payment_gateway || orderDetails?.gateway || orderDetails?.paymentMethodGateway || null;
  const gatewayKeyCandidate = rawGateway ? normalizePaymentKey(rawGateway) : (['razorpay', 'payu', 'stripe', 'paypal'].includes(normalizedKey) ? normalizedKey : null);
  const gatewayLabel = gatewayKeyCandidate ? (gatewayMap[gatewayKeyCandidate] || gatewayKeyCandidate.charAt(0).toUpperCase() + gatewayKeyCandidate.slice(1)) : null;

  return (
    <div className="min-h-full bg-gray-50">

      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-b-3xl">
        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={48} className="text-green-500" />
        </div>
        <h1 className="text-2xl font-bold text-center mb-1">Order Placed Successfully!</h1>
        <p className="text-green-100 text-center text-sm">Thank you for your order</p>
      </div>

      {/* ── Body ── */}
      <div className="p-4 space-y-4">

        {/* Order ID */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 text-center">
          <p className="text-xs text-blue-500 mb-1 font-medium tracking-wide uppercase">Order ID</p>
          <p className="text-xl font-bold text-blue-800">#{orderNumber || orderId}</p>
          <p className="text-xs text-blue-500 mt-2">{formatOrderDate()}</p>
        </div>

        {/* Order Items */}
        <div>
          <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
            <Package size={18} className="text-blue-600" />
            Order Items ({items?.length || 0})
          </h3>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {items?.map((item, index) => {
              const unitPrice = item.price || 0;
              const lineTotal = item.subtotal || (unitPrice * item.quantity).toFixed(2);
              const hasDiscount = item.discount;

              return (
                <div key={index} className="flex items-center gap-3 p-3">
                  <div className="flex-shrink-0">
                    {getOrderItemImage(item, 'small')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 text-sm leading-tight">{item.name}</p>
                    {(item.unit || item.weight) && (
                      <p className="text-xs text-gray-500">
                        {[item.weight, item.unit].filter(Boolean).join(' ')} ({unitPrice ? `₹${unitPrice}` : '' })
                      </p>
                    )}
                    <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                    {hasDiscount && (
                      <p className="text-xs mt-0.5">
                        <span className="text-gray-700 font-medium">₹{item.product?.sale_price}</span>
                        <span className="text-gray-400 line-through ml-1.5">₹{item.product?.price}</span>
                      </p>
                    )}
                  </div>
                  <span className="font-semibold text-gray-800 text-sm flex-shrink-0">₹{lineTotal}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Delivery Slot */}
        <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-xl">
          <Clock size={20} className="text-orange-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1">Delivery Slot</p>
            <p className="text-sm text-gray-700 flex items-center gap-1.5">
              <span>{selectedSlot.icon}</span>
              <span className="font-medium">{selectedSlot.label}</span>
              <span className="text-gray-500">({selectedSlot.time})</span>
            </p>
            <p className="text-xs text-orange-600 font-medium mt-1">{formattedDeliveryDate}</p>
          </div>
        </div>

        {/* Delivery Address */}
        {deliveryAddress && (
          <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-xl">
            <MapPin size={20} className="text-purple-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">Delivery Address</p>
              <p className="text-sm text-gray-700">
                {deliveryAddress.address || deliveryAddress.street || ''}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {[deliveryAddress.city, deliveryAddress.state].filter(Boolean).join(', ')}
                {deliveryAddress.pincode ? ` - ${deliveryAddress.pincode}` : ''}
              </p>
            </div>
          </div>
        )}

        {/* Payment Method */}
        <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
          <CreditCard size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-gray-700 mb-1">Payment Method</p>
            <p className="text-sm text-gray-700 flex items-center gap-1.5">
              <span>{selectedPayment.icon}</span>
              <span className="font-medium">{selectedPayment.label}</span>
              {gatewayLabel && (
                <span className="text-xs text-gray-500 ml-2">via {gatewayLabel}</span>
              )}
            </p>
            {walletAmountUsed > 0 && (
              <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1">
                <Wallet size={12} />
                ₹{walletAmountUsed} deducted from wallet
              </p>
            )}
            {paymentMethod === 'upi_cod' && (
              <div className="mt-3 flex flex-col items-center w-full">
                <div className="bg-white p-4 rounded-2xl shadow-lg border-2 border-gray-200 flex justify-center" style={{ maxWidth: 320, width: '100%' }}>
                  <img src={upiQrCode} alt="UPI QR Code" style={{ width: 200, height: 200, display: 'block', borderRadius: 8 }} />
                </div>
                {upiId && <p className="text-sm font-semibold text-gray-800 mt-2 text-center">{upiId}</p>}
                <p className="text-xs text-gray-600 mt-1 text-center font-medium">Scan this QR at delivery to pay</p>
              </div>
            )}
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-gradient-to-br from-blue-700 to-blue-900 rounded-xl p-4 text-white">
          <div className="flex justify-between items-center mb-2">
            <span className="text-blue-200 text-sm">Subtotal</span>
            <span className="font-semibold text-sm">₹{originalAmount || totalAmount}</span>
          </div>
          {couponCode && couponDiscount > 0 && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-blue-200 text-sm flex items-center gap-1">
                <Tag size={14} /> Coupon ({couponCode})
              </span>
              <span className="font-semibold text-sm text-green-300">-₹{couponDiscount}</span>
            </div>
          )}
          {walletAmountUsed > 0 && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-blue-200 text-sm flex items-center gap-1">
                <Wallet size={14} /> Wallet Credit
              </span>
              <span className="font-semibold text-sm text-green-300">-₹{walletAmountUsed}</span>
            </div>
          )}
          <div className="flex justify-between items-center mb-2">
            <span className="text-blue-200 text-sm">Shipping</span>
            <span className="font-semibold text-sm">
              {shippingCharge > 0 ? `₹${shippingCharge}` : <span className="text-green-300">FREE</span>}
            </span>
          </div>
          <div className="flex justify-between items-center mb-3">
            <span className="text-blue-200 text-sm">Tax (GST)</span>
            <span className="font-semibold text-sm">₹{taxAmount || 0}</span>
          </div>
          <div className="border-t border-blue-600 pt-3">
            <div className="flex justify-between items-center">
              <span className="font-bold text-base">Total Paid</span>
              <span className="text-xl font-bold">₹{Number(totalAmount || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Estimated Delivery */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 text-center">
          <p className="text-xs text-yellow-600 font-medium mb-1">Estimated Delivery</p>
          <p className="font-bold text-yellow-800 text-base">{formattedDeliveryDate}</p>
          <p className="text-xs text-yellow-600 mt-1">
            {selectedSlot.icon} {selectedSlot.label} · {selectedSlot.time}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={onViewOrders}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-xl font-bold text-base hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
          >
            <Package size={20} />
            Track Your Order
            <ChevronRight size={18} />
          </button>
          <button
            onClick={onContinueShopping}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 py-4 rounded-xl font-semibold text-base hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
          >
            <Home size={20} />
            Continue Shopping
          </button>
        </div>

        {/* Footer */}
        <div className="text-center pb-2">
          <p className="text-xs text-gray-500">
            We'll send you updates about your order via WhatsApp and Email
          </p>
          <button
            type="button"
            onClick={() => onContactSupport && onContactSupport()}
            className="text-xs text-blue-600 mt-1.5 hover:text-blue-700 font-semibold"
          >
            Questions? Contact Support
          </button>
        </div>

      </div>
    </div>
  );
};

export default OrderConfirmationModal;
